import React from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import {
  approvalModelNeedsThreshold,
  WORKFLOW_APPROVAL_MODELS,
  WORKFLOW_END_ROUTE,
  WORKFLOW_END_ROUTE_LABEL,
  WorkflowApprovalConfig,
  WorkflowApprovalModel,
  WorkflowCanvas,
} from "./workflowBuilderData";
import WorkflowPersonSourceField, { WorkflowPersonValue } from "./WorkflowPersonSourceField";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

interface WorkflowApprovalPanelProps {
  approval: WorkflowApprovalConfig;
  canvases: WorkflowCanvas[];
  currentCanvasId: string;
  onChange: (approval: WorkflowApprovalConfig) => void;
}

const WorkflowApprovalPanel: React.FC<WorkflowApprovalPanelProps> = ({
  approval,
  canvases,
  currentCanvasId,
  onChange,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const routeOptions = canvases.filter((canvas) => canvas.id !== currentCanvasId);

  const handleModelChange = (event: SelectChangeEvent) => {
    const model = event.target.value as WorkflowApprovalModel;
    // A threshold only means something for "any N" (a count) and "percentage" (a percent).
    onChange({ ...approval, model, threshold: approvalModelNeedsThreshold(model) ? approval.threshold : null });
  };

  const handleThresholdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = Number(event.target.value);
    onChange({
      ...approval,
      threshold: event.target.value === "" || !Number.isFinite(parsed) ? null : parsed,
    });
  };

  const handleApproverChange = ({ source, reference }: WorkflowPersonValue) => {
    // The approver always has a source; the select offers no "none" option.
    if (source) onChange({ ...approval, source, approvers: reference });
  };

  const handleRouteChange = (event: SelectChangeEvent) => {
    onChange({ ...approval, [event.target.name]: event.target.value });
  };

  const renderRoute = (
    id: string,
    label: string,
    name: "acceptRouteId" | "rejectRouteId" | "cancelRouteId",
  ) => (
    <Box className={`workflow-canvas-dialog__route workflow-canvas-dialog__route--${id}`}>
      <Typography component="label">{label}</Typography>
      <FormControl disabled={viewOnly} size="small" fullWidth>
        <InputLabel id={`${currentCanvasId}-${id}-route-label`}>Route to</InputLabel>
        <Select
          labelId={`${currentCanvasId}-${id}-route-label`}
          name={name}
          label="Route to"
          value={approval[name]}
          onChange={handleRouteChange}
        >
          <MenuItem value="" className="workflow-canvas-dialog__placeholder-option">
            Select a canvas
          </MenuItem>
          {routeOptions.map((canvas) => (
            <MenuItem key={canvas.id} value={canvas.id}>
              {canvas.name}
            </MenuItem>
          ))}
          <MenuItem value={WORKFLOW_END_ROUTE}>{WORKFLOW_END_ROUTE_LABEL}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <Box className="workflow-canvas-dialog__approval-panel">
      <Box className="workflow-canvas-dialog__approval-grid">
        <FormControl disabled={viewOnly} size="small" fullWidth>
          <InputLabel id={`${currentCanvasId}-approval-model-label`}>Approval model</InputLabel>
          <Select
            labelId={`${currentCanvasId}-approval-model-label`}
            label="Approval model"
            value={approval.model}
            onChange={handleModelChange}
          >
            {WORKFLOW_APPROVAL_MODELS.map((model) => (
              <MenuItem key={model.value} value={model.value}>
                {model.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {approvalModelNeedsThreshold(approval.model) && (
          <TextField
            disabled={viewOnly}
            type="number"
            size="small"
            label={approval.model === "percentage" ? "Percent that must approve" : "How many must approve"}
            value={approval.threshold ?? ""}
            onChange={handleThresholdChange}
            inputProps={
              approval.model === "percentage" ? { min: 1, max: 100 } : { min: 1, step: 1 }
            }
          />
        )}
      </Box>

      <WorkflowPersonSourceField
        idPrefix={`${currentCanvasId}-approver`}
        sourceLabel="Approver comes from"
        value={{ source: approval.source, reference: approval.approvers }}
        onChange={handleApproverChange}
        multipleUsers
      />

      <Box className="workflow-canvas-dialog__routes">
        <Typography className="workflow-canvas-dialog__routes-title">Gates — route to</Typography>
        {renderRoute("accept", "Accept", "acceptRouteId")}
        {renderRoute("reject", "Reject", "rejectRouteId")}
        {renderRoute("cancel", "Cancel", "cancelRouteId")}
      </Box>

      <Box className="workflow-canvas-dialog__approval-note">
        On decision, the runtime stamps name, date/time, and IP address.
      </Box>
    </Box>
  );
};

export default WorkflowApprovalPanel;
